import { useState } from "react";
import { Plus, Minus, Phone, ArrowUpRight, HelpCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type FAQ = {
  q: string;
  a: string;
};
const FAQS: FAQ[] = [{
  q: "Can a single damaged panel be replaced, or do I need a whole new door?",
  a: "In most cases, just the damaged panel — that's the whole point of a sectional door's design. We carry 150+ replacement panels for B&D Panelift, Steel-Line, Centurion, and Hörmann doors in common colours (white, primrose, paperbark, woodland grey). A single panel swap takes 60–90 minutes and costs roughly 30–40% of a full door replacement. We only recommend a new door if the damage is across multiple panels or the structural integrity is compromised."
}, {
  q: "How much does sectional door repair cost in Sydney?",
  a: "Most sectional repairs sit between $220 and $850 depending on the fault. Roller and hinge service runs $220–$320. Single panel replacement is $480–$780 (varies by panel size and colour). Weather seal renewal is $260–$420. Track straightening typically $320–$520. Full system overhaul (springs + cables + rollers + hinges) is around $750–$1,100. We always quote fixed-price on-site before any work starts."
}, {
  q: "How long does sectional door repair take?",
  a: "Most repairs are completed in 60–120 minutes on first visit. Roller and hinge swaps take 60–90 minutes. Single panel replacement runs 90–120 minutes. Track re-alignment is usually 60–120 minutes depending on damage. We stock common parts in the van so most jobs don't need a return trip — you're back to a working door same-day."
}, {
  q: "Do you service all sectional door brands?",
  a: "Yes — every major brand sold in Australia. B&D Panelift (Icon, Classic, Statesman, Vogue), Steel-Line (Therma-Range), Centurion (Vantage, Boss, Eco-Plus), Taurean (R-Series), Hörmann (LPU, LTE), plus Dominator, 4Ddoors, Newgate, and Superlift. Common brand parts are stocked at our Bass Hill workshop; rarer parts sourced within 48 hours."
}, {
  q: "Can you colour-match a replacement panel to my existing door?",
  a: "Yes — that's why we carry 150+ panels in the most common Sydney colours and profiles. For unusual colours or older doors where the original shade has faded slightly, we can either source an exact match from the manufacturer (5–10 business days) or recommend a strategic panel placement to disguise minor variation. Send us a photo and we'll tell you which option suits."
}, {
  q: "My sectional door makes a loud grinding noise — what's causing it?",
  a: "Almost always one of three things: worn nylon rollers (cheapest fix, biggest noise reduction), dry or damaged hinges (need lubrication or replacement), or worn drum bearings. We carry roller and hinge stock in the van for all major brands, so most grinding-noise complaints are fixed first-visit in 60–90 minutes. The door is typically whisper-quiet afterwards."
}, {
  q: "Do you fix sectional doors after vehicle impact damage?",
  a: "Yes — this is one of our most common jobs. We assess the damage on-site: if only 1–2 panels are damaged, single-panel replacement is the cheapest and quickest fix. If tracks are bent, we can usually straighten them or replace as needed. We also work directly with insurance companies — provide a detailed written quote suitable for claims and can liaise with assessors if needed."
}, {
  q: "What warranty do you offer on sectional repairs?",
  a: "Every sectional repair includes a 12-month written workmanship warranty, plus the manufacturer's warranty on any new parts (panels, hinges, rollers, springs — typically 12–24 months). The warranty card is part of your written invoice. If anything we installed or repaired fails inside the warranty period, we come back at no charge — including labour."
}];
export default function SectionalFAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(faq => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a
      }
    }))
  };
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />

      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 10% 20%, rgba(245,166,35,0.06) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: "rgba(255,255,255,0.02)",
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 1
    }}>
        FAQ
      </div>

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 64,
        alignItems: "end"
      }} className="sectional-faq-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                08 / Sectional questions
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              The sectional questions Sydney owners{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                ask us most.
              </span>
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            Don't see your question? Call or WhatsApp us — we answer sectional
            questions every day, no obligation.
          </p>
        </div>

        {/* === ACCORDION + STICKY CTA === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
        gap: 48,
        alignItems: "start"
      }} className="sectional-faq-body">
          {/* LEFT — Accordion */}
          <div style={{
          display: "flex",
          flexDirection: "column"
        }}>
            {FAQS.map((faq, i) => <FaqRow key={i} faq={faq} index={i} isOpen={open === i} isLast={i === FAQS.length - 1} onToggle={() => setOpen(open === i ? null : i)} />)}
          </div>

          {/* RIGHT — Sticky CTA */}
          <div className="sectional-faq-sticky" style={{
          position: "sticky",
          top: 100,
          padding: "40px 32px",
          background: NAVY_DEEP,
          border: `1px solid rgba(245,166,35,0.2)`,
          borderRadius: 4,
          overflow: "hidden"
        }}>
            <div aria-hidden style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 3,
            height: "100%",
            background: GOLD
          }} />

            <div aria-hidden style={{
            position: "absolute",
            top: "-30%",
            right: "-30%",
            width: 300,
            height: 300,
            background: `radial-gradient(circle, rgba(245,166,35,0.1) 0%, transparent 60%)`,
            pointerEvents: "none"
          }} />

            <div style={{
            position: "relative"
          }}>
              <div style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: "rgba(245,166,35,0.15)",
              border: `1px solid rgba(245,166,35,0.3)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20
            }}>
                <HelpCircle size={24} color={GOLD} strokeWidth={1.8} />
              </div>

              <div style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 12
            }}>
                Got a dented panel?
              </div>

              <h3 style={{
              fontFamily: FONT_HEAD,
              fontSize: 22,
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.25,
              letterSpacing: "-0.015em",
              margin: "0 0 16px"
            }}>
                Send a photo — save thousands.
              </h3>

              <p style={{
              fontFamily: FONT_BODY,
              fontSize: 14,
              color: "rgba(255,255,255,0.65)",
              lineHeight: 1.55,
              margin: "0 0 28px"
            }}>
                Free advice in 15 minutes. We'll tell you if a single panel
                swap will fix it instead of a full door replacement.
              </p>

              <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 10
            }}>
                <a href="tel:0485000933" style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "16px 24px",
                background: GOLD,
                color: NAVY,
                fontFamily: FONT_HEAD,
                fontSize: 15,
                fontWeight: 700,
                borderRadius: 8,
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
              }} onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
              }} onMouseLeave={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
              }}>
                  <Phone size={16} strokeWidth={2.5} />
                  0485 000 933
                </a>
                <Link to="#contact-us" style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "14px 24px",
                background: "transparent",
                color: "#ffffff",
                fontFamily: FONT_HEAD,
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 8,
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,0.25)",
                transition: "background 0.2s, border-color 0.2s"
              }} onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
              }} onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
              }}>
                  Free quote form
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </Link>
              </div>

              <div style={{
              marginTop: 28,
              paddingTop: 24,
              borderTop: "1px solid rgba(255,255,255,0.08)"
            }}>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: GOLD_DARK,
                marginBottom: 8
              }}>
                  Operating hours
                </div>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.6
              }}>
                  
                  
                  <div style={{
                  color: GOLD,
                  fontWeight: 600,
                  marginTop: 6
                }}>
                    24/7 emergency callouts
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .sectional-faq-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .sectional-faq-body {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .sectional-faq-sticky {
            position: static !important;
          }
        }
      `}</style>
    </section>;
}
function FaqRow({
  faq,
  index,
  isOpen,
  isLast,
  onToggle
}: {
  faq: FAQ;
  index: number;
  isOpen: boolean;
  isLast: boolean;
  onToggle: () => void;
}) {
  return <div style={{
    borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.08)"
  }}>
      <button onClick={onToggle} aria-expanded={isOpen} style={{
      width: "100%",
      padding: "28px 0",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      textAlign: "left",
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      gap: 20,
      alignItems: "center",
      color: "#ffffff",
      fontFamily: FONT_HEAD,
      transition: "color 0.2s"
    }} onMouseEnter={e => {
      (e.currentTarget.children[1] as HTMLElement).style.color = GOLD;
    }} onMouseLeave={e => {
      (e.currentTarget.children[1] as HTMLElement).style.color = isOpen ? GOLD : "#ffffff";
    }}>
        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: 13,
        fontWeight: 800,
        color: isOpen ? GOLD : "rgba(255,255,255,0.35)",
        letterSpacing: "0.08em",
        minWidth: 28,
        transition: "color 0.2s"
      }}>
          {String(index + 1).padStart(2, "0")}
        </span>

        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(16px, 1.4vw, 19px)",
        fontWeight: 600,
        color: isOpen ? GOLD : "#ffffff",
        lineHeight: 1.35,
        letterSpacing: "-0.005em",
        transition: "color 0.2s"
      }}>
          {faq.q}
        </span>

        <span style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: isOpen ? GOLD : "rgba(255,255,255,0.05)",
        border: isOpen ? `1px solid ${GOLD}` : "1px solid rgba(255,255,255,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.2s, border-color 0.2s"
      }}>
          {isOpen ? <Minus size={16} color={NAVY} strokeWidth={2.5} /> : <Plus size={16} color="#ffffff" strokeWidth={2.5} />}
        </span>
      </button>

      <div style={{
      maxHeight: isOpen ? 500 : 0,
      overflow: "hidden",
      transition: "max-height 0.4s ease, padding 0.3s ease",
      paddingBottom: isOpen ? 28 : 0
    }}>
        <div style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 20
      }}>
          <div style={{
          minWidth: 28
        }} />
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 15,
          color: "rgba(255,255,255,0.75)",
          lineHeight: 1.7,
          margin: 0,
          maxWidth: 680
        }}>
            {faq.a}
          </p>
          <div style={{
          width: 36
        }} />
        </div>
      </div>
    </div>;
}